"use client";

import { SLogo } from "@/components/atoms";
import HeaderItem from "@/components/atoms/HeaderItem";
import MenuIcon from "@/components/atoms/icons/MenuIcon";
import { NAVBAR_LINKS } from "@/constants/values";
import { cn } from "@/utils";
import React, { useCallback, useEffect, useState } from "react";
import CloseIcon from "@/components/atoms/icons/CloseIcon";
import { usePathname, useRouter } from "next/navigation";
import { LanguageSwitcher } from "@/components/molecules";
import { useTranslation } from "react-i18next";

const MainHeader = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const router = useRouter();
  const pathname = usePathname();
  const currentLocale = pathname.split("/")[1];

  useEffect(() => {
    if (pathname.includes("/blog")) {
      setActiveSection("blog");
    } else if (pathname.includes("/careers")) {
      setActiveSection("careers");
    } else {
      setActiveSection("home");
    }
  }, [pathname]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const scrollToSection = useCallback((id: string) => {
    setTimeout(() => {
      if (id === "home") {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        return;
      }

      const section = document.querySelector(`#${id}`);
      const header = document.querySelector(".sticky");

      if (section && header) {
        const headerHeight = header.clientHeight;
        const sectionTop = section.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = sectionTop - headerHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }, 100);
  }, []);

  const handleItemClick = useCallback(
    (id: string) => {
      if (id === "blog") {
        router.push(`/${currentLocale}/blog`);
        setActiveSection("blog");
        closeMenu();
        return;
      }

      if (id === "careers") {
        router.push(`/${currentLocale}/careers`);
        setActiveSection("careers");
        closeMenu();
        return;
      }

      if (id === "home") {
        setActiveSection("home");
        closeMenu();
        const shouldNavigateHome =
          pathname.includes("blog") ||
          pathname.includes("careers") ||
          pathname.startsWith("/blog/") ||
          pathname.startsWith("/careers/");
        if (shouldNavigateHome) {
          router.push(`/${currentLocale}`);
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }, 500);
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
        return;
      }

      const shouldNavigateHome =
        pathname.includes("blog") ||
        pathname.includes("careers") ||
        pathname.startsWith("/blog/");
      if (shouldNavigateHome) {
        router.push(`/${currentLocale}`);
        setTimeout(() => {
          scrollToSection(id);
        }, 800);
      } else {
        scrollToSection(id);
      }

      setActiveSection(id);
      closeMenu();
    },
    [router, pathname, closeMenu, scrollToSection, currentLocale]
  );

  const handleLogoClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      const isHome =
        pathname === `/${currentLocale}` ||
        pathname === `/${currentLocale}/` ||
        pathname === "/" ||
        pathname === "";

      if (isHome) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: "smooth" });
        setActiveSection("home");
        closeMenu();
      } else {
        setActiveSection("home");
        closeMenu();
      }
    },
    [pathname, currentLocale, closeMenu]
  );

  useEffect(() => {
    const isHome =
      pathname === `/${currentLocale}` ||
      pathname === `/${currentLocale}/` ||
      pathname === "/" ||
      pathname === "";

    if (!isHome) return;

    const handleScroll = () => {
      if (window.scrollY < 200) {
        setActiveSection("home");
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [pathname, currentLocale]);

  return (
    <div className="sticky top-0 z-50 bg-white py-4 px-6 shadow-md flex flex-col md:flex-row justify-between items-center">
      <div className="flex md:block items-center justify-between md:justify-center w-full">
        <SLogo locale={currentLocale} onClick={handleLogoClick} />
        <button
          className="md:hidden flex items-center justify-center w-10 h-10 text-rose"
          onClick={toggleMenu}
        >
          {isMenuOpen ? (
            <span className="font-bold">
              <CloseIcon />
            </span>
          ) : (
            <MenuIcon />
          )}
        </button>
      </div>
      <div
        className={cn(
          "flex-col md:flex-row md:flex md:items-center gap-4 transition-all duration-300",
          isMenuOpen ? "flex" : "hidden md:flex"
        )}
      >
        {NAVBAR_LINKS(t).map((record, index) => (
          <HeaderItem
            key={`navbar-item-${index}`}
            record={record}
            onClick={() => handleItemClick(record.id)}
            activeSection={activeSection}
          />
        ))}
      </div>
      <LanguageSwitcher />
    </div>
  );
};

export default MainHeader;
